import { useTranslation } from 'react-i18next'

import { TermosDaProposta } from '@/features/proposta/components/termos-da-proposta'
import type { ParticipacaoResumo } from '@/features/proposta/entities/ResumoProposta/resumo-participacoes-imobiliarias'
import type { TPropostaSchemaInput } from '@/features/proposta/schemas'
import { InfoItem } from '@/pages/proposta/resumo-proposta/components/info-item'
import { Icon } from '@/shared/components/icon'
import { Render } from '@/shared/components/render-if'
import { Button, Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui'

type TCardDetalhesPropostaProps = {
  resumoParticipacao: readonly ParticipacaoResumo[]
  assinaturaContrato: TPropostaSchemaInput['detalhesDaProposta']['assinaturaContrato']
  termosDaProposta: string
  observacoes?: string
  onEdit: (step: number) => void
}

const STEP_DETALHES_PROPOSTA = 2
export function CardDetalhesProposta({
  resumoParticipacao,
  assinaturaContrato,
  termosDaProposta,
  observacoes,
  onEdit,
}: Readonly<TCardDetalhesPropostaProps>) {
  const { t } = useTranslation('proposta')
  const modeloAssinatura = assinaturaContrato === 'Eletronica' ? 'Eletrônica' : 'Impressa'

  return (
    <Card className="shadow-none">
      <CardHeader className="bg-neutral-200 py-3">
        <div className="flex flex-col justify-between gap-3 md:flex-row md:items-center">
          <CardTitle className="text-base font-normal text-neutral-900">
            {t('resumo_proposta.detalhes_proposta.titulo')}
          </CardTitle>
          <Button
            variant="ghost"
            size="sm"
            className="text-primary border-primary border-1 hover:bg-neutral-200 md:border-none"
            onClick={() => onEdit(STEP_DETALHES_PROPOSTA)}
          >
            <Icon name="pencil" className="mr-1 h-4 w-4" />
            {t('resumo_proposta.editar')}
          </Button>
        </div>
      </CardHeader>

      <CardContent className="space-y-4 p-6">
        <Render.If condition={!!resumoParticipacao.length}>
          {() => (
            <>
              <h3 className="text-base text-neutral-800">
                {t('resumo_proposta.detalhes_proposta.label.participacao_imobiliaria')}
              </h3>
              {resumoParticipacao.map((imob) => (
                <div key={imob.id} className="border-b border-neutral-300">
                  <InfoItem
                    rotulo={t('resumo_proposta.detalhes_proposta.label.imobiliaria')}
                    valor={imob.nome}
                    bordaInferior
                    className="pt-1 pb-2"
                  />
                  <div className="grid grid-cols-2 gap-4 pt-1 pb-2 md:grid-cols-3">
                    <InfoItem rotulo={imob.tipo} valor={imob.documento} bordaDireita />
                    <InfoItem
                      rotulo={t('resumo_proposta.detalhes_proposta.label.participacao')}
                      valor={imob.participacao}
                      bordaDireita
                      className="border-none md:border-r"
                    />
                    <InfoItem rotulo={t('resumo_proposta.detalhes_proposta.label.paga_por')} valor={imob.pagaPor} />
                  </div>
                </div>
              ))}
            </>
          )}
        </Render.If>

        <h3 className="text-base text-neutral-800">{t('resumo_proposta.detalhes_proposta.label.assinatura')}</h3>
        <InfoItem
          rotulo={t('resumo_proposta.detalhes_proposta.label.modelo_assinatura')}
          valor={modeloAssinatura}
          bordaInferior
          className="space-y-1 border-neutral-300 pt-1 pb-2"
        />

        <h3 className="mb-4 text-base text-neutral-800">
          {t('resumo_proposta.detalhes_proposta.label.termos_da_proposta')}
        </h3>
        <TermosDaProposta className="pb-3 text-sm text-neutral-800" termos={termosDaProposta} assinaturaContrato="-" />
        <Render.If condition={!!observacoes}>
          {() => (
            <div className="border-t border-neutral-300 pt-4">
              <h3 className="text-base text-neutral-800">{t('resumo_proposta.detalhes_proposta.label.observacoes')}</h3>
              <ul className="mt-4 list-disc space-y-1 pb-3 pl-6">
                <li className="text-sm text-neutral-800">{observacoes}</li>
              </ul>
            </div>
          )}
        </Render.If>
      </CardContent>
    </Card>
  )
}
